import type { SessionInfo } from "./session-state"

import { sseManager } from "../lib/sse-manager"

import {
  activeParentSessionId,
  activeSessionId,
  agents,
  clearActiveSession,
  clearActiveParentSession,
  clearInstanceSessionSelection,
  clearInstanceDraftPromptValues,
  clearInstanceDraftPrompts,
  clearSessionDraftPrompt,
  ensureSessionAncestorsExpanded,
  getPersistentExpandedSessionIds,
  ensureSessionExpanded,
  expandedSessions,
  getActiveParentSession,
  getActiveSession,
  getChildSessions,
  getDescendantSessions,
  getSessionRoot,
  getParentSessions,
  getSessionDraftPrompt,
  getSessionDraftPromptsForInstance,
  getAuthoritativeDraftSessionIdsForInstance,
  getAuthoritativelyDeletedSessionIdsForInstance,
  getAuthoritativeSessionExpansionIdsForInstance,
  hasAuthoritativeSessionSelection,
  hydrateActiveSessionSelection,
  hydrateSessionIdleMarkers,
  hydrateSessionGenerationRecovery,
  hydrateSessionExpansion,
  beginSessionGenerationAdmission,
  cancelSessionGenerationAdmissions,
  getSessionFamily,
  getSessionInfo,
  getSessionListError,
  getSessionMessagesLoadError,
  getSessionSearchQuery,
  getSessionSearchSessions,
  getSessionThreads,
  getThreadTotals,
  getSessions,
  getVisibleSessionIds,
  isSessionMessagesLoading,
  isSessionExpanded,
  loading,
  markSessionIdleSeen,
  markViewedSessionIdleSeen,
  providers,
  sessionInfoByInstance,
  sessions,
  setActiveParentSession,
  setActiveSession,
  setActiveSessionFromList,
  setSessionDraftPrompt,
  setSessionExpanded,
  setSessionStatus,
  toggleSessionExpanded,
  clearSessionSearch,
  getSessionHasMore,
  hydrateSessionDraftPrompt,
  isSessionSearchLoading,
  onSessionDraftHydrated,
  resetSessionPagination,
  clearInstanceDeletedSessionAuthority,
  clearInstanceSessionExpansionState,
} from "./session-state"
import { isSessionBusy } from "./session-status"

import { getDefaultModel } from "./session-models"
import { handleWorktreeReady, reloadWorktrees } from "./worktrees"
import {
  createSession,
  deleteSession,
  fetchAgents,
  fetchProviders,
  getActiveCatalogLocation,
  refreshSessionCatalog,
  fetchSessions,
  hydrateRestoredSessionChain,
  hasMoreMessages,
  getMessageNextCursor,
  loadMoreMessages,
  loadMoreSessions,
  loadAllSessions,
  searchSessions,
  forkSession,
  loadMessages,
  loadOlderMessageWindow,
  loadNewerMessageWindow,
  loadLatestMessageWindow,
  loadOldestMessageWindow,
  loadMessageAnchor,
  isLatestMessageWindow,
  clearSessionListRequestState,
  clearSessionCatalogState,
} from "./session-api"
import {
  abortSession,
  backgroundSession,
  executeCustomCommand,
  renameSession,
  runShellCommand,
  sendMessage,
  updateSessionAgent,
  updateSessionModel,
} from "./session-actions"
import {
  handleNativeSessionEvent,
  handlePermissionReplied,
  handlePermissionUpdated,
  handleSessionCompacted,
  handleSessionDeleted,
  handleSessionError,
  handleSessionUpdate,
  handleTuiToast,
} from "./session-events"

sseManager.onNativeSessionEvent = handleNativeSessionEvent
sseManager.onSessionUpdate = handleSessionUpdate
sseManager.onSessionCompacted = handleSessionCompacted
sseManager.onSessionDeleted = handleSessionDeleted
sseManager.onSessionError = handleSessionError
sseManager.onTuiToast = handleTuiToast
sseManager.onPermissionUpdated = handlePermissionUpdated
sseManager.onPermissionReplied = handlePermissionReplied
sseManager.onWorktreeReady = handleWorktreeReady
sseManager.onWorktreeUpdated = async (instanceId) => {
  await reloadWorktrees(instanceId)
  await fetchSessions(instanceId, { reset: true })
}

export {
  abortSession,
  backgroundSession,
  activeParentSessionId,
  activeSessionId,
  agents,
  clearActiveSession,
  clearActiveParentSession,
  clearInstanceSessionSelection,
  clearInstanceDraftPromptValues,
  clearInstanceDraftPrompts,
  clearSessionDraftPrompt,
  createSession,
  deleteSession,
  ensureSessionAncestorsExpanded,
  getPersistentExpandedSessionIds,
  ensureSessionExpanded,
  expandedSessions,
  executeCustomCommand,
  renameSession,
  runShellCommand,
  fetchAgents,
  fetchProviders,
  getActiveCatalogLocation,
  refreshSessionCatalog,
  fetchSessions,
  hydrateRestoredSessionChain,
  hasMoreMessages,
  getMessageNextCursor,
  loadMoreMessages,
  loadOlderMessageWindow,
  loadNewerMessageWindow,
  loadLatestMessageWindow,
  loadOldestMessageWindow,
  loadMessageAnchor,
  isLatestMessageWindow,
  loadMoreSessions,
  loadAllSessions,
  searchSessions,
  forkSession,
  getActiveParentSession,
  getActiveSession,
  getChildSessions,
  getDescendantSessions,
  getSessionRoot,
  getDefaultModel,
  getParentSessions,
  getSessionDraftPrompt,
  getSessionDraftPromptsForInstance,
  getAuthoritativeDraftSessionIdsForInstance,
  getAuthoritativelyDeletedSessionIdsForInstance,
  getAuthoritativeSessionExpansionIdsForInstance,
  hasAuthoritativeSessionSelection,
  hydrateActiveSessionSelection,
  hydrateSessionIdleMarkers,
  hydrateSessionGenerationRecovery,
  hydrateSessionExpansion,
  beginSessionGenerationAdmission,
  cancelSessionGenerationAdmissions,
  getSessionFamily,
  getSessionInfo,
  getSessionListError,
  getSessionMessagesLoadError,
  getSessionSearchQuery,
  getSessionSearchSessions,
  getSessionThreads,
  getThreadTotals,
  getSessions,
  getVisibleSessionIds,
  isSessionBusy,
  isSessionMessagesLoading,
  isSessionExpanded,
  loadMessages,
  clearSessionListRequestState,
  clearSessionCatalogState,
  loading,
  markSessionIdleSeen,
  markViewedSessionIdleSeen,
  providers,
  sendMessage,
  sessionInfoByInstance,
  sessions,
  setActiveParentSession,
  setActiveSession,
  setActiveSessionFromList,
  setSessionDraftPrompt,
  setSessionExpanded,
  setSessionStatus,
  toggleSessionExpanded,
  updateSessionAgent,
  updateSessionModel,
  clearSessionSearch,
  getSessionHasMore,
  hydrateSessionDraftPrompt,
  isSessionSearchLoading,
  onSessionDraftHydrated,
  resetSessionPagination,
  clearInstanceDeletedSessionAuthority,
  clearInstanceSessionExpansionState,
}
export type { SessionInfo }
